import { useCallback, useEffect, useMemo, useState } from 'react';
import Topbar from '@/components/platform/Topbar';
import { LoadingScreen, RequireAdmin } from '@/components/platform/Guards';
import { deleteMember, listMembers, updateMemberStatus } from '@/lib/weddingApi';
import { STATUS_LABEL, formatDate, type MemberStatus, type WeddingMember } from '@/types/wedding';

type Filter = 'all' | MemberStatus;

const FILTERS: { key: Filter; label: string }[] = [
  { key: 'all', label: '전체' },
  { key: 'pending', label: '승인 대기' },
  { key: 'approved', label: '승인 완료' },
  { key: 'rejected', label: '승인 거절' },
];

function AdminBody() {
  const [members, setMembers] = useState<WeddingMember[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filter, setFilter] = useState<Filter>('all');
  const [busyId, setBusyId] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const all = await listMembers();
      setMembers(all.filter((m) => m.role === 'member'));
    } catch (err) {
      setError(err instanceof Error ? err.message : '회원 목록을 불러오지 못했어요.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const counts = useMemo(
    () => ({
      all: members.length,
      pending: members.filter((m) => m.status === 'pending').length,
      approved: members.filter((m) => m.status === 'approved').length,
      rejected: members.filter((m) => m.status === 'rejected').length,
    }),
    [members],
  );

  const shown = filter === 'all' ? members : members.filter((m) => m.status === filter);

  const handleStatus = async (id: string, status: MemberStatus) => {
    setBusyId(id);
    setError('');
    try {
      await updateMemberStatus(id, status);
      setMembers((prev) => prev.map((m) => (m.id === id ? { ...m, status } : m)));
    } catch (err) {
      setError(err instanceof Error ? err.message : '상태 변경에 실패했어요.');
    } finally {
      setBusyId('');
    }
  };

  const handleDelete = async (member: WeddingMember) => {
    const ok = window.confirm(`${member.name || member.email} 회원을 삭제할까요? 되돌릴 수 없어요.`);
    if (!ok) return;
    setBusyId(member.id);
    setError('');
    try {
      await deleteMember(member.id);
      setMembers((prev) => prev.filter((m) => m.id !== member.id));
    } catch (err) {
      setError(err instanceof Error ? err.message : '삭제에 실패했어요.');
    } finally {
      setBusyId('');
    }
  };

  return (
    <div className="pf-page">
      <Topbar />
      <main className="pf-main">
        <div className="pf-narrow">
          <div className="pf-page-head">
            <h1>회원 관리</h1>
            <p>가입한 회원을 승인하거나 관리할 수 있어요. 승인된 회원만 청첩장을 만들 수 있어요.</p>
          </div>

          <div className="pf-grid-3" style={{ marginBottom: 24 }}>
            <div className="pf-stat">
              <span className="num">{counts.pending}</span>
              <span className="lbl">승인 대기</span>
            </div>
            <div className="pf-stat">
              <span className="num">{counts.approved}</span>
              <span className="lbl">승인 완료</span>
            </div>
            <div className="pf-stat">
              <span className="num">{counts.all}</span>
              <span className="lbl">전체 회원</span>
            </div>
          </div>

          <div className="pf-card">
            <div className="pf-card-head">
              <h2>회원 목록</h2>
              <button type="button" className="pf-btn sm ghost" onClick={() => void load()}>
                <i className="ri-refresh-line" aria-hidden="true" />
                새로고침
              </button>
            </div>

            <div className="pf-filter-row">
              {FILTERS.map((f) => (
                <button
                  key={f.key}
                  type="button"
                  className={`pf-chip ${filter === f.key ? 'is-active' : ''}`}
                  onClick={() => setFilter(f.key)}
                >
                  {f.label} {counts[f.key]}
                </button>
              ))}
            </div>

            {error ? (
              <div className="pf-alert err" role="alert" style={{ marginBottom: 16 }}>
                <i className="ri-error-warning-line" aria-hidden="true" />
                <span>{error}</span>
              </div>
            ) : null}

            {loading ? (
              <div className="pf-empty">
                <div className="pf-spinner" aria-hidden="true" />
                <p className="pf-muted">회원 목록을 불러오는 중이에요…</p>
              </div>
            ) : shown.length === 0 ? (
              <div className="pf-empty">
                <i className="ri-inbox-line" aria-hidden="true" />
                <p className="pf-muted">해당하는 회원이 없어요.</p>
              </div>
            ) : (
              <div className="pf-table-wrap">
                <table className="pf-table">
                  <thead>
                    <tr>
                      <th>이름</th>
                      <th>이메일</th>
                      <th>연락처</th>
                      <th>가입일</th>
                      <th>상태</th>
                      <th>관리</th>
                    </tr>
                  </thead>
                  <tbody>
                    {shown.map((m) => (
                      <tr key={m.id}>
                        <td className="cell-name">{m.name || '-'}</td>
                        <td>{m.email}</td>
                        <td>{m.phone || '-'}</td>
                        <td>{formatDate(m.created_at)}</td>
                        <td>
                          <span className={`pf-badge ${m.status}`}>{STATUS_LABEL[m.status]}</span>
                        </td>
                        <td>
                          <div className="cell-actions">
                            {m.status !== 'approved' ? (
                              <button
                                type="button"
                                className="pf-btn sm primary"
                                disabled={busyId === m.id}
                                onClick={() => void handleStatus(m.id, 'approved')}
                              >
                                <i className="ri-check-line" aria-hidden="true" />
                                승인
                              </button>
                            ) : null}
                            {m.status !== 'rejected' ? (
                              <button
                                type="button"
                                className="pf-btn sm"
                                disabled={busyId === m.id}
                                onClick={() => void handleStatus(m.id, 'rejected')}
                              >
                                <i className="ri-close-line" aria-hidden="true" />
                                거절
                              </button>
                            ) : null}
                            <button
                              type="button"
                              className="pf-btn sm danger"
                              disabled={busyId === m.id}
                              onClick={() => void handleDelete(m)}
                            >
                              <i className="ri-delete-bin-line" aria-hidden="true" />
                              삭제
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}

export default function AdminPage() {
  return (
    <RequireAdmin>
      <AdminBody />
    </RequireAdmin>
  );
}